
import { Box, CircularProgress, Typography, Alert, Paper } from '@mui/material'
import { useLogin } from '@refinedev/core'
import { getAuth, signInWithEmailLink, isSignInWithEmailLink } from 'firebase/auth'
import React, { useEffect, useState } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'

import { FIREBASE_CONFIG } from '@/app/config/firebase.ts'

export const EmailLinkCompletePage: React.FC = () => {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { mutate: login } = useLogin()

  useEffect(() => {
    const completeEmailLinkSignIn = async () => {
      const auth = getAuth()
      const currentUrl = window.location.href

      // Check if this URL contains the required parameters for our magic link
      const apiKey = searchParams.get('apiKey')
      const oobCode = searchParams.get('oobCode')
      const mode = searchParams.get('mode')

      if (!apiKey || !oobCode || mode !== 'signIn') {
        // Fallback to Firebase email link check
        if (!isSignInWithEmailLink(auth, currentUrl)) {
          setError('Invalid authentication link. Please request a new login link.')
          setLoading(false)
          return
        }
      }

      try {
        // Ensure tenant ID is set before authentication for multi-tenancy
        // This is critical for generating tenant-aware JWT tokens
        const { tenantId } = FIREBASE_CONFIG
        auth.tenantId = tenantId

        // Get the email from localStorage (stored when sending the link)
        let email = window.localStorage.getItem('emailForSignIn')

        // If email is not in localStorage, try to get from URL params or prompt user
        if (!email) {
          email = searchParams.get('email')
        }

        if (!email) {
          // As a fallback, prompt user for email
          email = window.prompt('Please confirm your email for sign-in')
        }

        if (!email) {
          throw new Error('Email is required to complete sign-in')
        }

        // Complete the sign in with email link using Firebase
        // The auth.tenantId setting ensures this generates a tenant-aware JWT token
        const result = await signInWithEmailLink(auth, email, currentUrl)

        // Get the ID token (this should now be tenant-aware)
        const idToken = await result.user.getIdToken()

        // Log the token for debugging (remove in production)
        console.warn('Authentication successful for tenant:', tenantId)
        console.warn('User:', result.user.uid)
        console.warn('Token contains tenant ID:', !!result.user.tenantId)

        // Clear the email from localStorage
        window.localStorage.removeItem('emailForSignIn')

        // Use Refine's login hook with the token
        login({ token: idToken })

        // Note: Refine will handle the redirect automatically after successful login
      } catch (error: unknown) {
        console.error('Error completing email link sign-in:', error)
        let errorMessage = 'Failed to complete sign-in. '

        const firebaseError = error as { code?: string; message?: string }

        if (firebaseError.code === 'auth/invalid-action-code') {
          errorMessage += 'The login link is invalid or has expired.'
        } else if (firebaseError.code === 'auth/invalid-email') {
          errorMessage += 'Invalid email address.'
        } else if (firebaseError.code === 'auth/user-disabled') {
          errorMessage += 'This account has been disabled.'
        } else {
          errorMessage += firebaseError.message || 'Please try again.'
        }

        setError(errorMessage)
      } finally {
        setLoading(false)
      }
    }

    completeEmailLinkSignIn()
  }, [searchParams, navigate, login])

  if (loading) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="100vh"
        bgcolor="background.default"
      >
        <Paper
          elevation={3}
          sx={{
            p: 4,
            textAlign: 'center',
            minWidth: 300,
          }}
        >
          <CircularProgress size={48} sx={{ mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            Completing Sign In...
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Please wait while we verify your authentication link.
          </Typography>
        </Paper>
      </Box>
    )
  }

  if (error) {
    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        minHeight="100vh"
        bgcolor="background.default"
      >
        <Paper elevation={3} sx={{ p: 4, maxWidth: 400 }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="h6" gutterBottom>
              Authentication Failed
            </Typography>
            <Typography variant="body2">{error}</Typography>
          </Alert>
          <Box textAlign="center" mt={2}>
            <Typography variant="body2" color="text.secondary">
              You can{' '}
              <span
                style={{
                  color: 'primary.main',
                  cursor: 'pointer',
                  textDecoration: 'underline',
                }}
                onClick={() => navigate('/login')}
              >
                return to login
              </span>{' '}
              to request a new link.
            </Typography>
          </Box>
        </Paper>
      </Box>
    )
  }

  // This should not be reached, but just in case
  return null
}
